import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ShieldAlert, ShieldCheck, Shield, RefreshCw, Activity, Mail, AlertTriangle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

// Banner colors + icon per risk level
const LEVELS = {
  good:     { bg: 'bg-emerald-50',  border: 'border-emerald-200', text: 'text-emerald-900', Icon: ShieldCheck,  label: 'Healthy — safe to send' },
  warning:  { bg: 'bg-amber-50',    border: 'border-amber-300',   text: 'text-amber-900',   Icon: ShieldAlert,  label: 'Caution — review before sending' },
  critical: { bg: 'bg-rose-50',     border: 'border-rose-300',    text: 'text-rose-900',    Icon: AlertTriangle, label: 'Critical — blasts blocked' },
};

const SenderHealthCard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/crm/sender-health?days=30`, { credentials: 'include' });
      if (res.ok) setData(await res.json());
    } finally { setLoading(false); }
  }, []);

  const handleReset = async () => {
    if (!window.confirm(
      "Reset Sender Health?\n\n" +
      "This clears all webhook event history (bounces, opens, clicks) so the dashboard recalculates from scratch.\n\n" +
      "Use this if your bounce/open rates are showing stale data from misconfigured early sends.\n\n" +
      "Auto-excluded contacts will be re-enabled (manual unsubscribes are preserved). This cannot be undone."
    )) return;
    try {
      const res = await fetch(`${API}/api/crm/sender-health/reset`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({}),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.detail || 'Reset failed');
      toast.success(result.message || 'Health metrics reset');
      load();
    } catch (err) { toast.error(err.message || 'Reset failed'); }
  };

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return (
      <Card><CardContent className="py-4 flex items-center gap-2 text-sm text-slate-500">
        <RefreshCw size={14} className="animate-spin" /> Checking sender health…
      </CardContent></Card>
    );
  }
  if (!data) return null;

  const level = LEVELS[data.risk_level] || LEVELS.good;
  const { Icon } = level;
  const last = data.last_webhook_event;
  const lastDate = last?.received_at ? new Date(last.received_at) : null;
  const hoursAgo = lastDate ? Math.floor((Date.now() - lastDate.getTime()) / 3600000) : null;

  const fmtPct = (n) => `${(n ?? 0).toFixed(2)}%`;

  return (
    <Card className={`${level.border} ${level.bg} border-2`} data-testid="sender-health-card">
      <CardContent className="pt-4 pb-3">
        {/* Top banner */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <Icon className={`${level.text} flex-shrink-0`} size={28} />
            <div className="flex-1 min-w-0">
              <div className={`font-bold text-sm ${level.text} uppercase tracking-wide`}>
                Sender Health: {level.label}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Marketing sender: <code className="bg-white/70 px-1 rounded text-[11px]">{data.sender_config?.campaign_sender || '—'}</code>
                {data.sender_config?.isolated_subdomain
                  ? <span className="ml-2 text-emerald-700 font-medium">✓ Isolated</span>
                  : <span className="ml-2 text-amber-700 font-medium">⚠ Same root as real email</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={load} data-testid="sender-health-refresh" title="Refresh">
              <RefreshCw size={14} />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleReset} data-testid="sender-health-reset" title="Reset health metrics (wipes stale events)">
              <Trash2 size={14} className="text-rose-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(s => !s)} data-testid="sender-health-toggle">
              {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </Button>
          </div>
        </div>

        {/* Sample-size notice */}
        {data.sample_size != null && data.sample_size < (data.min_sample_for_risk || 20) && (
          <div className="mt-3 ml-9 text-xs text-slate-600 bg-white/70 rounded px-2 py-1.5 inline-block">
            ℹ️ Health based on only <strong>{data.sample_size}</strong> tracked event{data.sample_size === 1 ? '' : 's'} (need {data.min_sample_for_risk || 20}+ for reliable scoring). Rates shown for reference only — risk-blocking is disabled until more data accumulates.
          </div>
        )}

        {/* Risk reasons (always visible if any) */}
        {(data.risk_reasons || []).length > 0 && (
          <ul className={`mt-3 ml-9 space-y-0.5 text-xs ${level.text}`}>
            {data.risk_reasons.map((r, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <span className="mt-1">•</span><span>{r}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Quick stat grid */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4 pt-3 border-t border-white/40" data-testid="sender-health-stats">
          <Stat label="Bounce rate" value={fmtPct(data.rates.bounce_pct)} threshold={data.rates.bounce_pct < 2 ? 'good' : data.rates.bounce_pct < 5 ? 'warn' : 'bad'} />
          <Stat label="Spam complaints" value={fmtPct(data.rates.spam_pct)} threshold={data.rates.spam_pct < 0.1 ? 'good' : data.rates.spam_pct < 0.3 ? 'warn' : 'bad'} sub={`${data.totals.spam_reports} reports`} />
          <Stat label="Unsubscribe rate" value={fmtPct(data.rates.unsub_pct)} threshold={data.rates.unsub_pct < 0.5 ? 'good' : data.rates.unsub_pct < 1.5 ? 'warn' : 'bad'} />
          <Stat label="Open rate" value={fmtPct(data.rates.open_pct)} threshold={data.rates.open_pct > 18 ? 'good' : data.rates.open_pct > 10 ? 'warn' : 'bad'} />
          <Stat label="Click rate" value={fmtPct(data.rates.click_pct)} threshold={data.rates.click_pct > 2 ? 'good' : data.rates.click_pct > 0.8 ? 'warn' : 'bad'} />
        </div>

        {/* Expanded detail */}
        {expanded && (
          <div className="mt-4 pt-3 border-t border-white/40 space-y-3">
            <Section icon={Mail} title="Domain Authentication (SendGrid)">
              {!data.domain_auth?.checked && (
                <div className="text-xs text-amber-700">SendGrid API check could not be performed (API key missing or scope limited).</div>
              )}
              {data.domain_auth?.checked && data.domain_auth.domains.length === 0 && (
                <div className="text-xs text-rose-700">❌ No authenticated sending domain. Settings → Sender Authentication → Authenticate Your Domain.</div>
              )}
              {(data.domain_auth?.domains || []).map((d, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="font-mono">{d.subdomain ? `${d.subdomain}.` : ''}{d.domain}</span>
                  <span className={d.valid ? 'text-emerald-700 font-semibold' : 'text-rose-700 font-semibold'}>
                    {d.valid ? '✓ Verified' : '⚠ Pending'}
                  </span>
                </div>
              ))}
            </Section>

            <Section icon={Activity} title="Webhook Connection">
              {last ? (
                <div className="text-xs text-slate-700">
                  Last event: <span className="font-semibold">{last.event}</span> · {hoursAgo === 0 ? 'just now' : hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.floor(hoursAgo/24)}d ago`}
                  {hoursAgo > 72 && <span className="ml-2 text-amber-700 font-medium">⚠ Stale — webhook may be misconfigured</span>}
                </div>
              ) : (
                <div className="text-xs text-rose-700">⚠ No webhook events ever received. SendGrid → Settings → Mail Settings → Event Webhook → POST to <code>https://bcs-us.com/api/crm/sendgrid-events</code></div>
              )}
            </Section>

            <Section icon={Shield} title="List Health">
              <div className="text-xs text-slate-700">
                <strong>{data.list_health.active_contacts.toLocaleString()}</strong> active · <strong>{data.list_health.suppressed_contacts.toLocaleString()}</strong> suppressed ({data.list_health.suppression_rate_pct}%)
              </div>
            </Section>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const Stat = ({ label, value, threshold, sub }) => {
  const color = threshold === 'good' ? 'text-emerald-700' : threshold === 'warn' ? 'text-amber-700' : 'text-rose-700';
  return (
    <div className="bg-white/70 rounded-lg p-2.5">
      <div className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">{label}</div>
      <div className={`text-lg font-bold ${color}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
};

const Section = ({ icon: Icon, title, children }) => (
  <div>
    <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 mb-1">
      <Icon size={12} /> {title}
    </div>
    <div className="ml-5 space-y-1">{children}</div>
  </div>
);

export default SenderHealthCard;
